import { useState } from "react";
import { motion } from "framer-motion";
import {
  FileCheck,
  Home,
  Building2,
  ChevronRight,
  CheckCircle,
  FileText,
  DollarSign,
  Clock,
} from "lucide-react";
import { Card, Button } from "@bcc/shared-ui";
import { formatCurrency } from "@bcc/shared-ui";
import { SEO } from "../components/SEO";

type PermitCategory = "residential" | "commercial";

interface PermitType {
  id: string;
  name: string;
  description: string;
  documents: string[];
  feeRange: { min: number; max: number };
  processingDays: number;
}

const residentialPermits: PermitType[] = [
  {
    id: "building",
    name: "Building Permit",
    description: "New construction, additions, renovations over $10,000",
    documents: [
      "Site plan",
      "Floor plans",
      "Building specifications",
      "Energy calculations",
    ],
    feeRange: { min: 150, max: 5000 },
    processingDays: 10,
  },
  {
    id: "development",
    name: "Development Permit",
    description: "Land use changes, variances, new developments",
    documents: [
      "Site plan",
      "Context plan",
      "Building elevations",
      "Landscape plan",
    ],
    feeRange: { min: 300, max: 2000 },
    processingDays: 40,
  },
  {
    id: "electrical",
    name: "Electrical Permit",
    description: "Electrical installations, panel upgrades, rewiring",
    documents: ["Electrical drawings", "Load calculations"],
    feeRange: { min: 50, max: 500 },
    processingDays: 5,
  },
  {
    id: "plumbing",
    name: "Plumbing Permit",
    description: "New fixtures, drain lines, water services",
    documents: ["Plumbing drawings", "Fixture schedule"],
    feeRange: { min: 50, max: 400 },
    processingDays: 5,
  },
  {
    id: "secondary_suite",
    name: "Secondary Suite Permit",
    description: "Basement suite, garage suite, garden suite",
    documents: ["Floor plans", "Building code compliance report", "Site plan"],
    feeRange: { min: 200, max: 1500 },
    processingDays: 15,
  },
  {
    id: "deck",
    name: "Deck/Fence Permit",
    description: "Decks over 0.6m, fences over 1.85m",
    documents: ["Site plan", "Construction details", "Footing details"],
    feeRange: { min: 100, max: 300 },
    processingDays: 5,
  },
];

const commercialPermits: PermitType[] = [
  {
    id: "commercial_building",
    name: "Commercial Building Permit",
    description: "New commercial construction, major renovations",
    documents: [
      "Architect drawings",
      "Structural drawings",
      "M&E drawings",
      "Fire safety plan",
    ],
    feeRange: { min: 500, max: 50000 },
    processingDays: 20,
  },
  {
    id: "tenant_improvement",
    name: "Tenant Improvement Permit",
    description: "Interior modifications to commercial spaces",
    documents: ["Floor plan", "Reflected ceiling plan", "Occupancy details"],
    feeRange: { min: 200, max: 5000 },
    processingDays: 10,
  },
  {
    id: "change_of_use",
    name: "Change of Use Permit",
    description: "Converting building use (e.g., retail to restaurant)",
    documents: [
      "Proposed use details",
      "Parking analysis",
      "Code compliance review",
    ],
    feeRange: { min: 300, max: 2000 },
    processingDays: 30,
  },
  {
    id: "signage",
    name: "Sign Permit",
    description: "Commercial signage, fascia signs, freestanding signs",
    documents: ["Sign drawings", "Location plan", "Structural details"],
    feeRange: { min: 100, max: 500 },
    processingDays: 10,
  },
];

export function PermitInfoPage() {
  const [category, setCategory] = useState<PermitCategory>("residential");
  const [selectedPermit, setSelectedPermit] = useState<PermitType | null>(null);

  const permits =
    category === "residential" ? residentialPermits : commercialPermits;

  return (
    <>
      <SEO />
      <div className="space-y-6">
        {/* Header */}
        <div>
          <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
            <FileCheck className="h-6 w-6 text-teal-500" />
            Permit Requirements
          </h1>
          <p className="text-gray-500 mt-1">
            Find out what permits you need and required documentation for your
            project.
          </p>
        </div>

        {/* Category Toggle */}
        <div className="flex gap-4">
          <button
            onClick={() => {
              setCategory("residential");
              setSelectedPermit(null);
            }}
            className={`flex-1 p-4 rounded-xl border-2 transition-all ${
              category === "residential"
                ? "border-teal-500 bg-teal-50"
                : "border-gray-200 hover:border-gray-300"
            }`}
          >
            <Home
              className={`h-8 w-8 mx-auto mb-2 ${
                category === "residential" ? "text-teal-600" : "text-gray-400"
              }`}
            />
            <p className="font-semibold text-gray-900">Residential</p>
            <p className="text-sm text-gray-500">Houses, suites, additions</p>
          </button>

          <button
            onClick={() => {
              setCategory("commercial");
              setSelectedPermit(null);
            }}
            className={`flex-1 p-4 rounded-xl border-2 transition-all ${
              category === "commercial"
                ? "border-teal-500 bg-teal-50"
                : "border-gray-200 hover:border-gray-300"
            }`}
          >
            <Building2
              className={`h-8 w-8 mx-auto mb-2 ${
                category === "commercial" ? "text-teal-600" : "text-gray-400"
              }`}
            />
            <p className="font-semibold text-gray-900">Commercial</p>
            <p className="text-sm text-gray-500">Offices, retail, industrial</p>
          </button>
        </div>

        <div className="grid lg:grid-cols-2 gap-6">
          {/* Permit List */}
          <div className="space-y-3">
            <h2 className="font-semibold text-gray-900">
              {category === "residential" ? "Residential" : "Commercial"}{" "}
              Permits
            </h2>
            {permits.map((permit, index) => (
              <motion.div
                key={permit.id}
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: index * 0.05 }}
              >
                <Card
                  variant="outlined"
                  className={`cursor-pointer transition-all ${
                    selectedPermit?.id === permit.id
                      ? "border-teal-500 bg-teal-50"
                      : "hover:border-gray-300"
                  }`}
                  onClick={() => setSelectedPermit(permit)}
                >
                  <div className="flex items-center justify-between">
                    <div>
                      <h3 className="font-medium text-gray-900">
                        {permit.name}
                      </h3>
                      <p className="text-sm text-gray-500">
                        {permit.description}
                      </p>
                    </div>
                    <ChevronRight className="h-5 w-5 text-gray-400" />
                  </div>
                </Card>
              </motion.div>
            ))}
          </div>

          {/* Permit Details */}
          <div>
            {selectedPermit ? (
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
              >
                <Card variant="elevated" className="sticky top-24">
                  <h2 className="text-xl font-semibold text-gray-900 mb-4">
                    {selectedPermit.name}
                  </h2>
                  <p className="text-gray-600 mb-6">
                    {selectedPermit.description}
                  </p>

                  {/* Fee and Timeline */}
                  <div className="grid grid-cols-2 gap-4 mb-6">
                    <div className="p-3 bg-gray-50 rounded-lg">
                      <div className="flex items-center gap-2 text-gray-500 mb-1">
                        <DollarSign className="h-4 w-4" />
                        <span className="text-sm">Estimated Fee</span>
                      </div>
                      <p className="font-semibold text-gray-900">
                        {formatCurrency(selectedPermit.feeRange.min)} -{" "}
                        {formatCurrency(selectedPermit.feeRange.max)}
                      </p>
                    </div>
                    <div className="p-3 bg-gray-50 rounded-lg">
                      <div className="flex items-center gap-2 text-gray-500 mb-1">
                        <Clock className="h-4 w-4" />
                        <span className="text-sm">Processing Time</span>
                      </div>
                      <p className="font-semibold text-gray-900">
                        ~{selectedPermit.processingDays} business days
                      </p>
                    </div>
                  </div>

                  {/* Required Documents */}
                  <div>
                    <h3 className="font-medium text-gray-900 mb-3 flex items-center gap-2">
                      <FileText className="h-4 w-4 text-teal-500" />
                      Required Documents
                    </h3>
                    <ul className="space-y-2">
                      {selectedPermit.documents.map((doc, index) => (
                        <li
                          key={index}
                          className="flex items-center gap-2 text-gray-600"
                        >
                          <CheckCircle className="h-4 w-4 text-green-500" />
                          {doc}
                        </li>
                      ))}
                    </ul>
                  </div>

                  <div className="mt-6 pt-4 border-t border-gray-100">
                    <Button
                      variant="primary"
                      className="w-full bg-teal-600 hover:bg-teal-700"
                    >
                      Start Application Checklist
                    </Button>
                  </div>
                </Card>
              </motion.div>
            ) : (
              <Card variant="outlined" className="text-center py-12">
                <FileCheck className="h-12 w-12 text-gray-300 mx-auto mb-4" />
                <p className="text-gray-500">
                  Select a permit type to see requirements
                </p>
              </Card>
            )}
          </div>
        </div>
      </div>
    </>
  );
}
